import * as React from 'react';

import { pt } from 'date-fns/locale';
import { DayPicker } from 'react-day-picker';

import { cn } from '../../utils/cn';

import { buttonVariants } from './button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select.jsx';

function Calendar({ className, classNames, showOutsideDays = true, ...props }) {
  const currentDate = new Date();

  const handleCalendarChange = (_value, _e) => {
    const _event = {
      target: {
        value: String(_value),
      },
    };

    _e(_event);
  };

  return (
    <DayPicker
      {...props}
      onSelect={(val) => {
        props.onSelect(val);

        if (props?.onAfterSelect) {
          props.onAfterSelect();
        }
      }}
      locale={pt}
      mode="single"
      showOutsideDays={true}
      className={cn('p-3', className)}
      captionLayout="dropdown"
      startMonth={new Date(1930, 1)}
      hideNavigation={true}
      endMonth={new Date(currentDate.getFullYear() + 5, 12)}
      defaultMonth={
        props?.selected
          ? new Date(
              props?.selected?.getFullYear(),
              props?.selected?.getMonth(),
            )
          : currentDate
      }
      classNames={{
        dropdowns: 'flex flex-row justify-between gap-2',
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-sm font-medium text-center',
        table: 'w-full border-collapse space-y-1',
        weekdays: 'flex',
        weekday:
          'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]',
        week: 'flex w-full mt-2',
        day: 'flex justify-center items-center rounded-lg size-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-9 p-0 font-normal aria-selected:opacity-100',
        ),
        selected:
          'bg-primary text-primary-foreground !hover:bg-red-500 !hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        outside:
          'day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
        today: 'bg-accent text-accent-foreground',
        disabled: 'text-muted-foreground opacity-50',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Dropdown: ({ ...props }) => {
          return (
            <Select
              {...props}
              onValueChange={(value) => {
                if (props.onChange) {
                  handleCalendarChange(value, props.onChange);
                }
              }}
              value={props.value}
            >
              <SelectTrigger
                className={cn(
                  buttonVariants({ variant: 'ghost' }),
                  'mr-1 h-7 w-fit py-2 pl-2 pr-1 font-medium [.is-between_&]:hidden [.is-end_&]:hidden [.is-start.is-end_&]:flex',
                )}
              >
                <SelectValue placeholder={props?.caption}>
                  {props?.caption}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="scrolling-auto max-h-[var(--radix-popper-available-height);] min-w-[var(--radix-popper-anchor-width)] overflow-y-auto">
                {props.options &&
                  props.options.map((child, i) => (
                    <SelectItem
                      key={i}
                      value={child?.value}
                      className="min-w-[var(--radix-popper-anchor-width)]"
                    >
                      {child?.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          );
        },
      }}
    />
  );
}

Calendar.displayName = 'Calendar';

export { Calendar };
